'use client';

import * as blotout from 'components/analytics/blotout';
import * as gtm from 'components/analytics/gtm';
import { createCheckoutUrl, useFunnelCart } from 'components/hooks/use-funnel-cart';
import ButtonArrow from 'components/icons/button-arrow';
import LoadingDots from 'components/loading-dots';
import { withSuspense } from 'components/with-suspense';
import { Product } from 'lib/bigcommerce/types';
import { createUrl } from 'lib/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { CSSProperties, FormEvent, MouseEvent, useState, useTransition } from 'react';
import { cleanQueryParams } from 'shared-react-components';

export function SubmitButton({
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonText,
  buttonFontSize,
  pending
}: {
  buttonColor: string;
  buttonSize: string;
  buttonEmbellish: string;
  buttonText: string;
  buttonFontSize?: number;
  pending: boolean;
}) {
  let buttonStyle = {};
  if (buttonFontSize && buttonFontSize > 0) {
    buttonStyle = {
      'fontSize': `${buttonFontSize}px`
    }
  }

  return (
    <>
      <AddToCartButton
        buttonColor={buttonColor}
        buttonSize={buttonSize}
        buttonEmbellish={buttonEmbellish}
        buttonStyle={buttonStyle}
        buttonText={buttonText}
        pending={pending}
      />
    </>
  );
}

function AddToCartButton({
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonStyle,
  buttonText,
  pending
}: {
  buttonColor: string;
  buttonSize: string;
  buttonEmbellish: string;
  buttonStyle: CSSProperties | undefined;
  buttonText?: string;
  pending: boolean;
}) {
  let embelishStyle = '';
  if (buttonColor.match(/(^4P.+)/))
  embelishStyle = `cta-4p-button-embellish-${buttonColor.replace(/(^4P)/,'').replace(/(\s)/g,'').toLowerCase()}`;

  return (
    <>
    { embelishStyle && buttonEmbellish === 'Circle' && (
      <img src="https://cdn.builder.io/api/v1/image/assets%2F1adbd5971dac4273891f273603729777%2Fab43a3fc60de4a7aa41dde3b4c17d3ac" className={`cta-4p-button-embellish cta-4p-button-embellish-circle ${embelishStyle}`} alt=""/>
    )}
    { embelishStyle && buttonEmbellish === 'Arrows' && (
      <span className={`cta-4p-button-embellish cta-4p-button-embellish-arrow cta-4p-button-embellish-arrow-left ${embelishStyle}`}>
        <ButtonArrow />
      </span>
    )}
    <button
      aria-label={buttonText}
      className={getClassName(buttonColor, buttonSize, buttonEmbellish)}
      style={buttonStyle}
      onClick={(e: React.FormEvent<HTMLButtonElement>) => {
        if (pending) e.preventDefault();
      }}
    >
      {buttonText}
      {pending && <LoadingDots className="loading-dots-dark" />}
    </button>
    { embelishStyle && buttonEmbellish === 'Arrows' && (
      <span className={`cta-4p-button-embellish cta-4p-button-embellish-arrow cta-4p-button-embellish-arrow-right ${embelishStyle}`}>
        <ButtonArrow />
      </span>
    )}
    </>
  );
}

function getClassName(buttonColor: string, buttonSize: string, buttonEmbellish: string) {
  let isFancy = false;
  let buttonClass = '';
  switch (buttonColor){
    case 'Primary':
      buttonClass = 'cta-button cta-button-atc';
      break;
    case 'Primary Inverted':
      buttonClass = 'cta-button cta-button-inverted';
      break;
    case 'Secondary':
      buttonClass = 'cta-button cta-button-secondary';
      break;
    case 'Secondary Inverted':
      buttonClass = 'cta-button cta-button-secondary-inverted';
      break;
    case 'ATC Tile':
      buttonClass = 'tile__atc';
      break;
    case '4P Orange 1':
      buttonClass = 'cta-button cta-4p-button cta-4p-button-orange1';
      isFancy = true;
      break;
    case '4P Orange 2':
      buttonClass = 'cta-button cta-4p-button cta-4p-button-orange2';
      isFancy = true;
      break;
    case '4P Green':
      buttonClass = 'cta-button cta-4p-button cta-4p-button-green';
      isFancy = true;
      break;
  }
  if (isFancy){
    buttonClass += ` cta-4p-button-${buttonSize.toLowerCase()}`;
    if (buttonEmbellish !== 'None') 
      buttonClass += ` cta-4p-button-embellish-${buttonEmbellish.toLowerCase()}`;
  }

  return buttonClass;
}

export function AddToFunnelCartComponent({
  apiProduct,
  variantSku,
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonText,
  buttonFontSize,
  quantity,
  productLine,
  funnelName,
  lander,
  children
}: {
  apiProduct?: Product;
  variantSku: string;
  buttonColor: string;
  buttonSize: string;
  buttonEmbellish: string;
  buttonText: string;
  buttonFontSize: number | undefined;
  quantity?: number;
  productLine: string;
  funnelName: string;
  lander: string;
  children?: React.ReactNode
}) {
  const [message, setMessage] = useState<undefined | string>(undefined);
  const [isPending, startTransition] = useTransition();

  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const queryParams = Object.fromEntries(cleanQueryParams(searchParams));
  const [, _offers, productLineUrl, funnelNameUrl, landerUrl] = pathname.split('/');

  const { cartId } = useFunnelCart({
    sku: variantSku,
    quantity,
    productLine: productLine ? productLine : productLineUrl ?? '',
    funnelName: funnelName ? funnelName : funnelNameUrl ?? '',
    lander: lander ? lander : landerUrl,
    queryParams: searchParams
  });

  const getCheckoutUrl = async () => {
    let checkoutUrl: string | undefined;

    if (cartId) {
      checkoutUrl = createUrl('/funnel-checkout', new URLSearchParams({
        cartId,
        productLine: productLine ? productLine : productLineUrl ?? '',
        funnelName: funnelName ? funnelName : funnelNameUrl ?? '',
        lander: lander ? lander : landerUrl ?? '',
        ...(queryParams ? { ...queryParams } : {}),
      }));
    } else {
      console.debug('CartId not found, creating new cart and checkout url');
      const result = await createCheckoutUrl({
        sku: variantSku,
        quantity,
        productLine: productLine ? productLine : productLineUrl ?? '',
        funnelName: funnelName ? funnelName : funnelNameUrl ?? '',
        lander: lander ? lander : landerUrl,
        queryParams: searchParams
      });
      if (result?.isSuccess && result?.value?.checkoutUrl) {
        checkoutUrl = result?.value?.checkoutUrl
      } else {
        throw new Error(result?.error ?? 'An unexpected error occurred');
      }
    }
    return checkoutUrl;
  };

  const handleSubmit = async () => {
    const checkoutUrl = await getCheckoutUrl();

    if (checkoutUrl) {
      gtm.funnelAddToCart({ product: apiProduct, sku: variantSku, quantity: 1 });
      blotout.funnelAddToCart({ product: apiProduct, sku: variantSku, quantity: 1, url: checkoutUrl });
      window.location.href = checkoutUrl;
    } else {
      throw new Error('An unexpected error occurred');
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    startTransition(async () => {
      setMessage(undefined);
      try {
        await handleSubmit();
        setMessage('Success')
      } catch (error: any) {
        console.error(error);
        setMessage(error.message ?? 'An unexpected error occurred')
      }
    });
  };

  const markLinkVisited = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.currentTarget) {
      const childLink = event.currentTarget.querySelector('a');
      if (childLink) {
        childLink.classList.add('visited');
      }
    }
  }

  // intercept click events on children to handle the add to cart action
  const onClick = async (event: MouseEvent<any>) => {
    event.preventDefault();
    event.stopPropagation();

    setMessage(undefined);
    markLinkVisited(event);
    try {
      await handleSubmit();
      setMessage('Success')
    } catch (error: any) {
      console.error(error);
      setMessage(error.message ?? 'An unexpected error occurred')
    }
  };

  return (
    <>
      <div className="add-to-cart-box" >
        <form onSubmit={onSubmit}>
          <SubmitButton
            buttonColor={buttonColor}
            buttonSize={buttonSize}
            buttonEmbellish={buttonEmbellish}
            buttonText={buttonText}
            buttonFontSize={buttonFontSize}
            pending={isPending}
          />
          <p aria-live="polite" className="sr-only" role="status">
            {message}
          </p>
        </form>
      </div>
      {children && (
        <div onClickCapture={onClick}>
          {children}
        </div>
      )}
    </>
  );
}

export const AddToFunnelCart = withSuspense(AddToFunnelCartComponent, null);
